// src/pages/User/NewRequest/ui/hooks/usePurchaseStep.ts
import { useCallback, useMemo } from "react";
import { useFormikContext } from "formik";

import type { FormikInitialValuesNewRequest } from "../../model/form.model";
import type { CustomerOptions, FreightType } from "../../model/customer.model";

import { useNewRequestContext } from "../../state/NewRequestProvider";
import { useNewRequestController } from "./useNewRequestController";

import { getFreightPolicy } from "../../domain/policies/freightPolicy";

type PurchaseFieldPath =
  | "purchase.customerCnpj"
  | "purchase.deliveryAddressId"
  | "purchase.postalCode"
  | "purchase.district"
  | "purchase.cityState"
  | "purchase.withdrawBasisId"
  | "purchase.branchName"
  | "purchase.deliveryDate"
  | "purchase.freightType"
  | "purchase.responsibleName"
  | "purchase.responsiblePhone"
  | "purchase.responsibleEmail";

type SetPurchaseField = { path: PurchaseFieldPath; value: unknown };

function onlyDigits(v: string): string {
  return v.replace(/\D/g, "");
}

const CLEAR_ON_CUSTOMER_CLEAR: SetPurchaseField[] = [
  { path: "purchase.customerCnpj", value: "" },

  { path: "purchase.deliveryAddressId", value: "" },
  { path: "purchase.postalCode", value: "" },
  { path: "purchase.district", value: "" },
  { path: "purchase.cityState", value: "" },

  { path: "purchase.withdrawBasisId", value: "" },
  { path: "purchase.branchName", value: "" },

  { path: "purchase.deliveryDate", value: "" },

  // se no seu legado esses campos são derivados do backend e read-only,
  // pode remover estes três do reset.
  { path: "purchase.responsibleName", value: "" },
  { path: "purchase.responsiblePhone", value: "" },
  { path: "purchase.responsibleEmail", value: "" },
];

const CLEAR_DERIVED_BEFORE_LOAD: SetPurchaseField[] = [
  { path: "purchase.deliveryAddressId", value: "" },
  { path: "purchase.postalCode", value: "" },
  { path: "purchase.district", value: "" },
  { path: "purchase.cityState", value: "" },
  { path: "purchase.withdrawBasisId", value: "" },
  { path: "purchase.branchName", value: "" },
  { path: "purchase.deliveryDate", value: "" },
  { path: "purchase.responsibleName", value: "" },
  { path: "purchase.responsiblePhone", value: "" },
  { path: "purchase.responsibleEmail", value: "" },
];

export type CustomerInputChangeReason = "input" | "clear" | "reset" | "blur";
export type HandleCustomerInputArgs = {
  inputValue: string;
  reason: CustomerInputChangeReason;
};

export function usePurchaseStep() {
  const { state, dispatch } = useNewRequestContext();
  const formik = useFormikContext<FormikInitialValuesNewRequest>();
  const controller = useNewRequestController();

  const customer = state.purchase.customer;
  const values = formik.values.purchase;

  const companies = state.purchase.customersOptions;

  const isLoading = controller.state.isLoading;
  const customerSelected = Boolean(values.customerCnpj);
  const canInteract = customerSelected && !isLoading;

  const setPurchaseFields = useCallback(
    (fields: SetPurchaseField[]) => {
      for (const f of fields) formik.setFieldValue(f.path, f.value, false);
    },
    [formik]
  );

  const clearAllCustomerData = useCallback(() => {
    setPurchaseFields(CLEAR_ON_CUSTOMER_CLEAR);
    dispatch({ type: "reset/all" });
    // opcional: se tiver action no state pra limpar customer/availableDates
  }, [setPurchaseFields, dispatch]);

  const handleCustomerChange = useCallback(
    async (opt: CustomerOptions | null) => {
      const cnpj = opt?.key ? onlyDigits(opt.key) : "";

      // sempre sincroniza o form primeiro
      formik.setFieldValue("purchase.customerCnpj", cnpj, false);

      if (!cnpj) {
        clearAllCustomerData();
        return;
      }

      // evita “lixo” enquanto carrega o novo cliente
      setPurchaseFields(CLEAR_DERIVED_BEFORE_LOAD);

      await controller.action.selectCustomer(cnpj, formik);
    },
    [formik, controller, setPurchaseFields, clearAllCustomerData]
  );

  const handleCustomerInput = useCallback(
    ({ inputValue, reason }: HandleCustomerInputArgs) => {
      // "X" do MUI
      if (reason === "clear") {
        clearAllCustomerData();
        return;
      }

      // usuário apagou tudo no teclado (principalmente mobile)
      if (reason === "input" && onlyDigits(inputValue) === "") {
        clearAllCustomerData();
      }
    },
    [clearAllCustomerData]
  );

  const freightPolicy = useMemo(() => {
    return getFreightPolicy({
      customer,
      selectedFilialId: values.withdrawBasisId,
    });
  }, [customer, values.withdrawBasisId]);

  const onChangeDeliveryAddress = useCallback(
    (deliveryAddressId: string) => {
      formik.setFieldValue(
        "purchase.deliveryAddressId",
        deliveryAddressId,
        false
      );

      const addr = customer?.addresses?.find(
        (a) => String(a.id) === String(deliveryAddressId)
      );

      const basis = customer?.withdrawBasis?.find(
        (b) => String(addr?.filialIdDefault) === String(b.filialID)
      );

      setPurchaseFields([
        { path: "purchase.district", value: addr?.district ?? "" },
        {
          path: "purchase.cityState",
          value: addr ? `${addr.city} - ${addr.uf}` : "",
        },
        { path: "purchase.postalCode", value: addr?.postalCode ?? "" },

        { path: "purchase.responsiblePhone", value: addr?.phone ?? "" },
        { path: "purchase.responsibleEmail", value: addr?.email ?? "" },

        /* NOTE: AutoFillPurchase da base de retirada 
            Ao Trocar manualmente o Endereco de entrega,
            se faz necessario a troca automatica da base de retirada, 
            caso haja correspondência.
        */
        { path: "purchase.withdrawBasisId", value: basis?.shippingPoint ?? "" },
      ]);
    },
    [customer?.addresses, customer?.withdrawBasis, formik, setPurchaseFields]
  );

  const onChangeWithdrawBasis = useCallback(
    async (withdrawBasisId: string) => {
      const basis = customer?.withdrawBasis?.find(
        (b) => String(b.shippingPoint) === String(withdrawBasisId)
      );

      const filialID = basis?.filialID;

      const addr = filialID
        ? customer?.addresses?.find(
            (a) => String(a.filialIdDefault) === String(filialID)
          )
        : null;

      setPurchaseFields([
        { path: "purchase.withdrawBasisId", value: withdrawBasisId },
        { path: "purchase.branchName", value: basis?.branchName ?? "" },
        { path: "purchase.deliveryDate", value: "" },

        { path: "purchase.responsiblePhone", value: addr?.phone ?? "" },
        { path: "purchase.responsibleEmail", value: addr?.email ?? "" },

        /* NOTE: AutoFillPurchase do endereco de entrega 
            Ao Trocar manualmente a base de retirada,
            se faz necessario a troca automatica da o Endereco de entrega e seus deverivados, 
            caso haja correspondência.
        */
        { path: "purchase.deliveryAddressId", value: addr?.id ?? "" },
        { path: "purchase.postalCode", value: addr?.postalCode ?? "" },
        { path: "purchase.district", value: addr?.district ?? "" },
        {
          path: "purchase.cityState",
          value: addr ? `${addr.city} - ${addr.uf}` : "",
        },
      ]);

      await controller.action.reloadDates({
        withdrawBasisID: withdrawBasisId,
        freightType: values.freightType,
        formik,
      });
    },
    [
      customer?.withdrawBasis,
      customer?.addresses,
      controller,
      formik,
      values.freightType,
      setPurchaseFields,
    ]
  );

  const onChangeFreightType = useCallback(
    async (freightType: FreightType) => {
      setPurchaseFields([
        { path: "purchase.freightType", value: freightType },
        { path: "purchase.deliveryDate", value: "" },
      ]);

      if (values.withdrawBasisId && values.customerCnpj) {
        await controller.action.reloadDates({
          withdrawBasisID: values.withdrawBasisId,
          freightType,
          formik,
        });
      }
    },
    [
      controller,
      formik,
      setPurchaseFields,
      values.withdrawBasisId,
      values.customerCnpj,
    ]
  );

  return {
    // state
    canInteract,
    isLoading,
    error: state.purchase.error,

    // data
    companies,
    customer,
    availableDates: state.purchase.availableDates,
    freightPolicy,

    // bindings
    values,
    setFieldValue: formik.setFieldValue,

    // handlers
    clearAllCustomerData,
    handleCustomerChange,
    handleCustomerInput,
    onChangeDeliveryAddress,
    onChangeWithdrawBasis,
    onChangeFreightType,
  };
}
